import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
  flexRender,
} from "@tanstack/react-table";
import { apiClient } from "../services/api";
import { buttonStyle, buttonBackgroundColor } from "../components/Style";
import PageLayout from "../components/PageLayout";
import { Class } from "../types/Class";
import Dropdown from "../components/Dropdown";
import Badge from "../components/Badge";
import {
  tanstackTableStyle,
  getTanstackThStyle,
  sortHeaderStyle,
  sortArrowStyle,
  filterContainerStyle,
  tanstackTrStyle,
  getTanstackTdStyle,
  clickableCellStyle,
  emptyRowStyle,
} from "../components/Style";

const columnHelper = createColumnHelper<Class>();

function SupplementClassManagementPage() {
  const [classes, setClasses] = useState<Class[]>([]);
  const [classNameOptions, setClassNameOptions] = useState<string[]>([]);
  const [teacherOptions, setTeacherOptions] = useState<string[]>([]);
  const [classRoomOptions, setClassRoomOptions] = useState<string[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const navigate = useNavigate();

  // API에서 반 목록 가져오기
  const fetchClasses = async () => {
    try {
      const data = await apiClient.getClasses();
      // 보강반만 필터링
      const supplementClasses = data.filter((c: Class) => c.classCategory === "supplement");
      setClasses(supplementClasses);
      const uniqueNames = Array.from(
        new Set((supplementClasses || []).map((c: Class) => c.className).filter(Boolean))
      );
      setClassNameOptions(uniqueNames as string[]);
    } catch (error) {
      console.error("반 목록 조회 실패:", error);
    }
  };

  const fetchMeta = async () => {
    try {
      const [teachers, classrooms] = await Promise.all([
        apiClient.getTeachingTeachers(),
        apiClient.getClassClassrooms(),
      ]);
      setTeacherOptions((teachers || []).map((t: any) => t.username).filter(Boolean));
      setClassRoomOptions((classrooms || []).map((c: any) => c.classroomName).filter(Boolean));
    } catch (error) {
      console.error("메타데이터 조회 실패:", error);
    }
  };

  useEffect(() => {
    fetchClasses();
    fetchMeta();
  }, []);

  // 기본 정렬: 보강일 기준 (최근 날짜가 위로)
  const sortedClasses = useMemo(() => {
    return [...classes].sort((a, b) => {
      const aDate = a.classStartDate || "9999-12-31";
      const bDate = b.classStartDate || "9999-12-31";
      return bDate.localeCompare(aDate); // 내림차순
    });
  }, [classes]);

  // 24시간 형식을 12시간 형식으로 변환
  const formatTo12Hour = (time24: string): string => {
    if (!time24 || time24 === "00:00") return "-";
    const [hour, minute] = time24.split(":").map(Number);
    const isPM = hour >= 12;
    let hour12 = hour % 12;
    if (hour12 === 0) hour12 = 12;
    const period = isPM ? "오후" : "오전";
    return `${period} ${String(hour12).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  };

  // 강의 상태 계산
  const getClassStatus = (classStartDate?: string | null) => {
    if (!classStartDate) return "정보 없음";
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(classStartDate);
    startDate.setHours(0, 0, 0, 0);

    if (today < startDate) return "시작 예정";
    if (today.getTime() === startDate.getTime()) return "진행중";
    return "종료";
  };

  // 테이블 컬럼 정의
  const columns = useMemo(() => [
    columnHelper.accessor("className", {
      header: "반 이름",
      size: 140,
      cell: (info) => (
        <span
          style={clickableCellStyle}
          onClick={() => navigate(`/class/supplement/${info.row.original.id}`)}
        >
          {info.getValue()}
        </span>
      ),
      sortingFn: "alphanumeric",
      filterFn: "equalsString",
    }),
    columnHelper.accessor("teacher", {
      header: "담당 선생님",
      size: 70,
      cell: (info) => (
        <span
          style={clickableCellStyle}
          onClick={() => navigate(`/class/supplement/${info.row.original.id}`)}
        >
          {info.getValue()}
        </span>
      ),
      sortingFn: "alphanumeric",
      filterFn: "equalsString",
    }),
    columnHelper.accessor("classRoom", {
      header: "교실",
      size: 70,
      cell: (info) => (
        <span
          style={clickableCellStyle}
          onClick={() => navigate(`/class/supplement/${info.row.original.id}`)}
        >
          {info.getValue() || "-"}
        </span>
      ),
      sortingFn: "alphanumeric",
      filterFn: "equalsString",
    }),
    columnHelper.accessor("classStartDate", {
      header: "보강일",
      size: 130,
      cell: (info) => (
        <span
          style={clickableCellStyle}
          onClick={() => navigate(`/class/supplement/${info.row.original.id}`)}
        >
          {info.getValue() || "-"}
        </span>
      ),
      sortingFn: "alphanumeric",
    }),
    columnHelper.display({
      id: "startTime",
      header: "보강 시작 시각",
      size: 140,
      cell: (info) => {
        const c = info.row.original;
        const startTime = ((c.mondayStartTime as string) || "00:00").substring(0, 5);
        return (
          <span
            style={clickableCellStyle}
            onClick={() => navigate(`/class/supplement/${c.id}`)}
          >
            {formatTo12Hour(startTime)}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: "endTime",
      header: "보강 종료 시각",
      size: 140,
      cell: (info) => {
        const c = info.row.original;
        const endTime = ((c.mondayEndTime as string) || "00:00").substring(0, 5);
        return (
          <span
            style={clickableCellStyle}
            onClick={() => navigate(`/class/supplement/${c.id}`)}
          >
            {formatTo12Hour(endTime)}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: "status",
      header: "강의 상태",
      size: 120,
      cell: (info) => {
        const c = info.row.original;
        const status = getClassStatus(c.classStartDate);
        const statusBadgeProps: Record<string, { backgroundColor: string; textColor: string; borderColor: string }> = {
          "시작 예정": { backgroundColor: "#fff3e0", textColor: "#e65100", borderColor: "#ffcc80" },
          "진행중": { backgroundColor: "#e8f5e9", textColor: "#2e7d32", borderColor: "#a5d6a7" },
          "종료": { backgroundColor: "#f3f4f6", textColor: "#6b7280", borderColor: "#d1d5db" },
          "정보 없음": { backgroundColor: "#f3f4f6", textColor: "#6b7280", borderColor: "#d1d5db" },
        };
        const badgeProps = statusBadgeProps[status] || statusBadgeProps["정보 없음"];

        return (
          <Badge
            {...badgeProps}
            onClick={() => navigate(`/class/supplement/${c.id}`)}
          >
            {status}
          </Badge>
        );
      },
    }),
  ], [navigate]);

  // Table instance
  const table = useReactTable({
    data: sortedClasses,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <PageLayout
      title="보강반 관리"
      actionRows={[
        (
          <button
            key="add-class"
            onClick={() => navigate("/class/supplement/new")}
            style={{ ...buttonStyle, ...buttonBackgroundColor.save }}
          >
            + 반 추가
          </button>
        ),
      ]}
    >
      <style>
        {`
          .supplement-class-table tbody tr:hover {
            background-color: #f6f8fa;
          }
        `}
      </style>
      <div style={{ overflow: "auto", maxHeight: "calc(100vh - 180px)" }}>
        <table className="supplement-class-table" style={tanstackTableStyle}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const columnId = header.column.id;
                  
                  return (
                    <th
                      key={header.id}
                      style={getTanstackThStyle(false, header.getSize(), {})}
                    >
                      {header.isPlaceholder ? null : (
                        <div>
                          {/* Header with sort */}
                          {header.column.getCanSort() ? (
                            <div
                              onClick={header.column.getToggleSortingHandler()}
                              style={sortHeaderStyle}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: <span style={sortArrowStyle}>▲</span>,
                                desc: <span style={sortArrowStyle}>▼</span>,
                              }[header.column.getIsSorted() as string] ?? null}
                            </div>
                          ) : (
                            flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )
                          )}
                          
                          {/* Filter row */}
                          <div style={filterContainerStyle}>
                            {columnId === "className" && (
                              <Dropdown
                                value={(header.column.getFilterValue() as string) ?? ""}
                                onChange={(value) => header.column.setFilterValue(value || undefined)}
                                options={[
                                  { value: "", label: "전체" },
                                  ...classNameOptions.map((v) => ({ value: v, label: v })),
                                ]}
                              />
                            )}
                            {columnId === "teacher" && (
                              <Dropdown
                                value={(header.column.getFilterValue() as string) ?? ""}
                                onChange={(value) => header.column.setFilterValue(value || undefined)}
                                options={[
                                  { value: "", label: "전체" },
                                  ...teacherOptions.map((v) => ({ value: v, label: v })),
                                ]}
                              />
                            )}
                            {columnId === "classRoom" && (
                              <Dropdown
                                value={(header.column.getFilterValue() as string) ?? ""}
                                onChange={(value) => header.column.setFilterValue(value || undefined)}
                                options={[
                                  { value: "", label: "전체" },
                                  ...classRoomOptions.map((v) => ({ value: v, label: v })),
                                ]}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={emptyRowStyle}
                >
                  보강반이 없습니다.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} style={tanstackTrStyle}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={getTanstackTdStyle(false, cell.column.getSize())}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
}

export default SupplementClassManagementPage;
